// tournament-cloud.js — Cloud Tournaments (ecosystem Phase 2).
// Organizer creates a tournament and gets a 6-char JOIN CODE. Teams join from
// their own phones. Organizer locks entries → round-robin fixtures generate.
// Whichever participant scores a fixture posts its result summary into the
// tournament doc; every phone computes the points table + NRR + leaderboards
// locally from fixtures (pure functions — no concurrent-aggregate conflicts)
// and sees updates LIVE via onSnapshot. Spark-plan safe: no Cloud Functions.
import { db } from '../firebase-config.js';
import {
  doc, setDoc, getDoc, getDocs, deleteDoc, collection, query, where,
  runTransaction, onSnapshot
} from 'firebase/firestore';
import { get } from 'svelte/store';
import { currentUser } from '../svelte/store.js';

// No confusing chars (0/O, 1/I) — code is read out loud on the ground.
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

function randomJoinCode() {
  let code = '';
  const rand = new Uint8Array(6);
  crypto.getRandomValues(rand);
  for (const byte of rand) code += CODE_ALPHABET[byte % CODE_ALPHABET.length];
  return code;
}

/**
 * Creates a cloud tournament and atomically claims its join code.
 * @returns {Promise<Object>} the tournament doc
 */
export async function createCloudTournament({ name, overs, rules }) {
  const user = get(currentUser);
  if (!user?.uid) throw new Error('Login required to host a cloud tournament.');
  const cleanName = String(name || '').trim().slice(0, 40);
  if (!cleanName) throw new Error('Enter a tournament name.');

  const tid = doc(collection(db, 'tournaments')).id;
  const code = randomJoinCode();
  const data = {
    id: tid,
    code,
    name: cleanName,
    organizerUid: user.uid,
    organizerName: user.displayName || 'Organizer',
    overs: Number(overs) || 10,
    rules: rules || {},
    status: 'open', // 'open' → teams joining, 'locked' → fixtures live
    teams: [],
    participantUids: [user.uid],
    fixtures: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  await runTransaction(db, async (tx) => {
    const codeRef = doc(db, 'tournamentCodes', code);
    const snap = await tx.get(codeRef);
    if (snap.exists()) throw new Error('CODE_COLLISION');
    tx.set(codeRef, { tid, uid: user.uid, claimedAt: new Date().toISOString() });
    tx.set(doc(db, 'tournaments', tid), data);
  }).catch(err => {
    if (err?.message === 'CODE_COLLISION') throw new Error('Code clash — try again.');
    throw err;
  });
  return data;
}

/** Finds a tournament by its join code. */
export async function getCloudTournamentByCode(code) {
  const clean = String(code || '').trim().toUpperCase();
  if (!clean) return null;
  try {
    const codeSnap = await getDoc(doc(db, 'tournamentCodes', clean));
    if (!codeSnap.exists()) return null;
    const tSnap = await getDoc(doc(db, 'tournaments', codeSnap.data().tid));
    return tSnap.exists() ? tSnap.data() : null;
  } catch (error) {
    console.error('[CloudTournament] Lookup failed:', error);
    return null;
  }
}

/**
 * Joins a tournament with a team (any signed-in user, while status is open).
 * @param {Object} team - { name, username, players: [names] }
 */
export async function joinCloudTournament(tid, team) {
  const user = get(currentUser);
  if (!user?.uid) throw new Error('Login required to join.');
  const entry = {
    name: String(team?.name || '').trim().slice(0, 30),
    username: team?.username || '',
    ownerUid: user.uid,
    players: (team?.players || []).filter(Boolean).slice(0, 25)
  };
  if (!entry.name) throw new Error('Team name required.');
  if (entry.players.length < 2) throw new Error('Team needs at least 2 players.');

  await runTransaction(db, async (tx) => {
    const ref = doc(db, 'tournaments', tid);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Tournament not found.');
    const t = snap.data();
    if (t.status !== 'open') throw new Error('Entries are closed — fixtures already generated.');
    if ((t.teams || []).some(x => x.name.toLowerCase() === entry.name.toLowerCase())) {
      throw new Error(`Team "${entry.name}" is already in this tournament.`);
    }
    if ((t.teams || []).length >= 12) throw new Error('Tournament is full (12 teams max).');
    const teams = [...(t.teams || []), entry];
    const participantUids = t.participantUids.includes(user.uid)
      ? t.participantUids : [...t.participantUids, user.uid];
    tx.update(ref, { teams, participantUids, updatedAt: new Date().toISOString() });
  });
  return true;
}

/** Organizer locks entries and generates the round-robin fixtures. */
export async function lockAndGenerateFixtures(tournament) {
  const user = get(currentUser);
  if (!user?.uid || tournament.organizerUid !== user.uid) {
    throw new Error('Only the organizer can generate fixtures.');
  }
  return await runTransaction(db, async (tx) => {
    const ref = doc(db, 'tournaments', tournament.id);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Tournament not found.');
    const t = snap.data();
    if (t.status !== 'open') throw new Error('Entries are closed — fixtures already generated.');

    const names = (t.teams || []).map(tm => tm.name);
    if (names.length < 2) throw new Error('Need at least 2 teams.');

    const fixtures = [];
    let index = 1;
    for (let i = 0; i < names.length; i++) {
      for (let j = i + 1; j < names.length; j++) {
        fixtures.push({ id: `cf_${index++}`, teamA: names[i], teamB: names[j], played: false, result: null });
      }
    }
    tx.update(ref, { status: 'locked', fixtures, updatedAt: new Date().toISOString() });
    return fixtures;
  });
}

/**
 * Posts a finished fixture's result (any participant — whoever scored it).
 * Won't overwrite an already-posted result unless `force` is set (organizer
 * re-posting a corrected match).
 */
export async function postFixtureResult(tid, fixtureId, result, { force = false, _fromRetry = false } = {}) {
  const user = get(currentUser);
  if (!user?.uid) return false;
  try {
    await runTransaction(db, async (tx) => {
      const ref = doc(db, 'tournaments', tid);
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('Tournament not found.');
      const t = snap.data();
      const fixtures = (t.fixtures || []).map(f => {
        if (f.id !== fixtureId) return f;
        if (f.played && !force) throw new Error('ALREADY_POSTED');
        return { ...f, played: true, result };
      });
      tx.update(ref, { fixtures, updatedAt: new Date().toISOString() });
    });
    return true;
  } catch (error) {
    if (error?.message === 'ALREADY_POSTED') return true; // idempotent
    // runTransaction needs the network — offline it throws. Persist the result
    // and retry when the connection returns so a fixture scored on the ground
    // (no signal) still reaches everyone's points table later.
    if (!_fromRetry) enqueuePendingResult({ tid, fixtureId, result });
    console.warn('[CloudTournament] Result post failed (queued for retry):', error?.code || error);
    return false;
  }
}

// ─── Pending fixture-result retry (offline-scored tournament matches) ────
const PENDING_RESULTS_KEY = 'cs_pending_fixture_results';

function _readPendingResults() {
  try { return JSON.parse(localStorage.getItem(PENDING_RESULTS_KEY) || '[]'); } catch (_) { return []; }
}
function _writePendingResults(list) {
  try { localStorage.setItem(PENDING_RESULTS_KEY, JSON.stringify(list.slice(-100))); } catch (_) {}
}
function enqueuePendingResult(entry) {
  const list = _readPendingResults();
  // De-dupe by tid+fixtureId — keep only the latest result for a fixture.
  const filtered = list.filter(e => !(e.tid === entry.tid && e.fixtureId === entry.fixtureId));
  filtered.push({ ...entry, _queuedAt: Date.now() });
  _writePendingResults(filtered);
}

let _retryingResults = false;
export async function retryPendingFixtureResults() {
  if (_retryingResults || !navigator.onLine) return;
  const list = _readPendingResults();
  if (list.length === 0) return;
  _retryingResults = true;
  const remaining = [];
  for (const e of list) {
    const ok = await postFixtureResult(e.tid, e.fixtureId, e.result, { _fromRetry: true });
    if (!ok) remaining.push(e);
  }
  const latestList = _readPendingResults();
  const retryKeys = new Set(list.map(e => `${e.tid}_${e.fixtureId}`));
  const remainingKeys = new Set(remaining.map(e => `${e.tid}_${e.fixtureId}`));
  const merged = latestList.filter(e => {
    const key = `${e.tid}_${e.fixtureId}`;
    return remainingKeys.has(key) || !retryKeys.has(key);
  });
  _writePendingResults(merged);
  _retryingResults = false;
}

let _resultRetryInited = false;
export function initFixtureResultRetry() {
  if (_resultRetryInited) return;
  _resultRetryInited = true;
  window.addEventListener('online', () => setTimeout(retryPendingFixtureResults, 3000));
}

/** Live subscription — cb(tournament) on every change. Returns unsubscribe. */
export function listenCloudTournament(tid, cb) {
  return onSnapshot(doc(db, 'tournaments', tid),
    snap => { if (snap.exists()) cb(snap.data()); },
    error => console.warn('[CloudTournament] Listener error:', error));
}

/** Tournaments I organize or participate in. */
export async function fetchMyCloudTournaments() {
  const user = get(currentUser);
  if (!user?.uid) return [];
  try {
    const ref = collection(db, 'tournaments');
    const [mine, joined] = await Promise.all([
      getDocs(query(ref, where('organizerUid', '==', user.uid))),
      getDocs(query(ref, where('participantUids', 'array-contains', user.uid)))
    ]);
    const seen = new Set();
    const out = [];
    for (const snap of [mine, joined]) {
      snap.forEach(d => { if (!seen.has(d.id)) { seen.add(d.id); out.push(d.data()); } });
    }
    return out.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  } catch (error) {
    console.error('[CloudTournament] Fetch failed:', error);
    return [];
  }
}

/** Organizer-only delete (frees the join code too). */
export async function deleteCloudTournament(tournament) {
  const user = get(currentUser);
  if (!user?.uid || tournament.organizerUid !== user.uid) {
    throw new Error('Only the organizer can delete the tournament.');
  }
  await deleteDoc(doc(db, 'tournaments', tournament.id));
  try { await deleteDoc(doc(db, 'tournamentCodes', tournament.code)); } catch (_) {}
  return true;
}

// ─── PURE COMPUTATIONS (run identically on every phone) ─────────────────

/** Extracts a compact fixture-result summary from a finished match. */
export function buildFixtureResultFromMatch(match) {
  if (!match?.result || !Array.isArray(match.innings)) return null;
  const quota = (match.overs || 0) * 6;

  const innSummary = (inn, teamObj) => {
    if (!inn) return null;
    const teamSize = (teamObj?.players || []).length;
    const maxWkts = match.lms ? teamSize : Math.max(1, teamSize - 1);
    return {
      runs: inn.totalRuns, wickets: inn.wickets, balls: inn.balls,
      allOut: inn.wickets >= maxWkts
    };
  };

  // playerStats: batting + bowling lines for leaderboards.
  const playerStats = {};
  match.innings.forEach(inn => {
    inn.batsmen.forEach(b => {
      if ((b.balls || 0) + (b.runs || 0) === 0 && !b.out) return;
      const p = playerStats[b.name] ||= { name: b.name, runs: 0, balls: 0, fours: 0, sixes: 0, wickets: 0, runsConceded: 0, ballsBowled: 0, highestScore: 0 };
      p.runs += b.runs || 0; p.balls += b.balls || 0;
      p.fours += b.fours || 0; p.sixes += b.sixes || 0;
      if ((b.runs || 0) > p.highestScore) p.highestScore = b.runs;
    });
    inn.bowlers.forEach(bw => {
      if ((bw.balls || 0) === 0) return;
      const p = playerStats[bw.name] ||= { name: bw.name, runs: 0, balls: 0, fours: 0, sixes: 0, wickets: 0, runsConceded: 0, ballsBowled: 0, highestScore: 0 };
      p.wickets += bw.wickets || 0; p.runsConceded += bw.runs || 0; p.ballsBowled += bw.balls || 0;
    });
  });

  const innA = match.innings.find(i => i.battingTeam === 'A');
  const innB = match.innings.find(i => i.battingTeam === 'B');
  const winner = match.result.winner === 'A' ? match.teamA.name
    : match.result.winner === 'B' ? match.teamB.name : 'TIE';

  return {
    winner,
    margin: match.result.margin || 0,
    marginType: match.result.marginType || '',
    quotaBalls: quota,
    a: innSummary(innA, match.teamA),
    b: innSummary(innB, match.teamB),
    teamAName: match.teamA.name,
    teamBName: match.teamB.name,
    playerStats,
    matchId: match.id,
    postedAt: new Date().toISOString()
  };
}

/** Points table + NRR from played fixtures. Sorted: PTS desc, then NRR. */
export function computeCloudPointsTable(tournament) {
  const table = {};
  (tournament.teams || []).forEach(t => {
    table[t.name] = { name: t.name, played: 0, won: 0, lost: 0, tied: 0, pts: 0, runsFor: 0, ballsFor: 0, runsAgainst: 0, ballsAgainst: 0 };
  });

  (tournament.fixtures || []).filter(f => f.played && f.result).forEach(f => {
    const r = f.result;
    const rows = [
      { row: table[r.teamAName], own: r.a, opp: r.b },
      { row: table[r.teamBName], own: r.b, opp: r.a }
    ];
    rows.forEach(({ row, own, opp }) => {
      if (!row) return;
      row.played++;
      if (r.winner === 'TIE') { row.tied++; row.pts += 1; }
      else if (r.winner === row.name) { row.won++; row.pts += 2; }
      else { row.lost++; }
      // NRR convention: an all-out side is counted at its FULL quota of overs.
      if (own) { row.runsFor += own.runs; row.ballsFor += own.allOut ? r.quotaBalls : own.balls; }
      if (opp) { row.runsAgainst += opp.runs; row.ballsAgainst += opp.allOut ? r.quotaBalls : opp.balls; }
    });
  });

  return Object.values(table).map(row => {
    if ((tournament.overs || 0) === 0) return { ...row, nrr: 0 };
    const forRate = row.ballsFor > 0 ? (row.runsFor / row.ballsFor) * 6 : 0;
    const againstRate = row.ballsAgainst > 0 ? (row.runsAgainst / row.ballsAgainst) * 6 : 0;
    return { ...row, nrr: forRate - againstRate };
  }).sort((a, b) => (b.pts - a.pts) || (b.nrr - a.nrr));
}

/** Orange cap / purple cap / six-hitters across all played fixtures. */
export function computeCloudLeaderboards(tournament) {
  const agg = {};
  (tournament.fixtures || []).filter(f => f.played && f.result?.playerStats).forEach(f => {
    Object.values(f.result.playerStats).forEach(p => {
      const row = agg[p.name] ||= { name: p.name, runs: 0, balls: 0, fours: 0, sixes: 0, wickets: 0, runsConceded: 0, ballsBowled: 0, highestScore: 0 };
      row.runs += p.runs; row.balls += p.balls;
      row.fours += p.fours; row.sixes += p.sixes;
      row.wickets += p.wickets; row.runsConceded += p.runsConceded; row.ballsBowled += p.ballsBowled;
      if (p.highestScore > row.highestScore) row.highestScore = p.highestScore;
    });
  });
  const players = Object.values(agg);
  return {
    orangeCap: [...players].sort((a, b) => (b.runs - a.runs) || (a.balls - b.balls)).slice(0, 5),
    purpleCap: [...players].filter(p => p.ballsBowled > 0).sort((a, b) => (b.wickets - a.wickets) || (a.runsConceded - b.runsConceded)).slice(0, 5),
    sixerKing: [...players].filter(p => p.sixes > 0).sort((a, b) => b.sixes - a.sixes).slice(0, 5)
  };
}
